import { currentCardLayout } from 'src/utils/cardLayoutExperiment'
import ga from 'src/utils/pixels/gtag'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'

export type SearchClickSurface = 'list_card' | 'map_card' | 'map_marker'

/**
 * What the card SHOWED — flat PostHog props so badge lift can be read straight
 * off search_result_view (denominator) vs search_result_click (numerator)
 * without re-deriving the card from listing state later (listing state drifts;
 * the render at impression time is what the renter saw). Built by
 * `buildCardBadges` in src/utils/searchAnalytics.ts.
 */
export interface SearchCardBadges {
  deal_type?: string
  badge_discount: boolean
  /** OperatorPromoKind when the «Promoción del operador» pill rendered. */
  badge_operator_promo?: string
  badge_scarcity?: 'last_one' | 'few_left'
  /** «N consultas en 30 días» (branch-level recentInquiries) rendered. */
  badge_demand: boolean
  badge_verified: boolean
  review_score?: number
}
export type MapInteractionType = 'marker_open' | 'zoom' | 'pan'

/**
 * Fires when a user clicks through to a listing from a search surface. The
 * `surface` + `position` + `config_id` let PostHog answer "do map clicks
 * convert better than list clicks" and join to the server-side
 * SEARCH_IMPRESSION event (same configId + 1-based position). No dedup — every
 * click is a distinct intent signal.
 */
export function trackSearchResultClick(args: {
  surface: SearchClickSurface
  configId?: string
  locationId: string
  position?: number
  /** Search zone (city slug). The pre/post dimension for the directory
   * dilution guardrail — plan §4.2. Undefined on an unscoped map search. */
  zone?: string
  /** The cover photo the card served — the cover-photo bandit's arm
   * (spec 2026-09-23). Undefined when the card has no cover. */
  coverPhotoId?: string
  badges?: SearchCardBadges
}): void {
  ga.event('search_result_click', {
    surface: args.surface,
    // The `search_card_stacked` arm the renter saw (row = the photo-left card).
    card_layout: args.surface === 'list_card' ? currentCardLayout() : undefined,
    config_id: args.configId,
    location_id: args.locationId,
    position: args.position,
    zone: args.zone,
    cover_photo_id: args.coverPhotoId,
    ...args.badges
  })
}

/** Where a search-card WhatsApp pill rendered. One surface today. */
export type SearchCardWhatsappSurface = 'search_card'

/**
 * Discovery event for the WhatsApp pill on Traditional/Broker search-grid
 * cards. Fires on every tap — the conversion stack (`add_to_cart` +
 * `traditional_contact` / `broker_contact` + intent-only `generate_lead`)
 * fires beside it, session-deduped on the SAME `wa_cta:<configId>:<subEvent>`
 * key as the listing page, so a card tap + a listing tap count once. Read it
 * against `search_result_view` (same `surface`-less card impressions) for the
 * pill's CTR. Never in `TIER_ROLLUP_MAP`.
 */
export function trackSearchCardWhatsappClick(args: {
  surface: SearchCardWhatsappSurface
  dealType: string
  locationId: string
  configId?: string
  position?: number
  zone?: string
}): void {
  ga.event('search_card_whatsapp_click', {
    surface: args.surface,
    deal_type: args.dealType,
    location_id: args.locationId,
    configuration_id: args.configId,
    position: args.position,
    zone: args.zone
  })
}

/**
 * Fires on map exploration. `marker_open` is deduped once per (session,
 * dedupKey) so re-opening the same marker doesn't spam; zoom/pan are fired
 * from the map's `onIdleChange` callback — Google Maps' idle event only fires
 * after movement settles, so it is already the debounced "user moved the map"
 * signal (no separate debounce wrapper needed).
 *
 * `pan` is additionally deduped once per SESSION (2026-07-16): un-deduped it
 * produced 238k events / 30d — 94% of all search_map_interaction volume at
 * ~143 fires per panning user — burying `marker_open` (1.4k) and bloating
 * ingestion for zero analytical gain. Post-change `pan` is a boolean-ish
 * "explored the map by panning" session signal, not a movement counter.
 * `zoom` stays per-movement (modest volume, ~9 per person).
 */
export function trackMapInteraction(
  type: MapInteractionType,
  dedupKey?: string
): void {
  const key = dedupKey ?? (type === 'pan' ? 'session' : undefined)
  if (key && firedOncePerSession(`map:${type}:${key}`)) return
  ga.event('search_map_interaction', { type })
}

/**
 * Fires once per (session, configId) when a user advances a search card's photo
 * carousel — a boolean-ish "engaged the photos" signal, not a per-swipe count.
 */
export function trackCarouselAdvance(args: {
  configId: string
  surface: SearchClickSurface
}): void {
  if (firedOncePerSession(`carousel:${args.configId}`)) return
  ga.event('search_card_carousel_advance', {
    config_id: args.configId,
    surface: args.surface
  })
}

/**
 * Fires once per (session, surface, configId) when a search card becomes
 * visible. The "viewed" denominator for viewed-CTR — distinct from the server
 * SEARCH_IMPRESSION "served" set, which the server fires with no viewport
 * knowledge. Skips when configId is missing (no stable dedup key).
 *
 * `surface` defaults to 'list_card' (list cards fire on IntersectionObserver
 * ≥50% in SearchCard). Pass `'map_card'` from the map when a marker is opened
 * and its floating card is shown — that is the map-card impression, the missing
 * denominator that lets PostHog compute map-card CTR (search_result_click
 * surface='map_card' / search_result_view surface='map_card'). The dedup key is
 * surface-scoped so a config seen in BOTH the list and the map counts as one
 * impression on each surface, never blocking the other.
 */
export function trackSearchResultView(args: {
  configId?: string
  locationId: string
  position?: number
  surface?: SearchClickSurface
  /** Search zone (city slug) — the pre/post dimension for the directory
   * dilution guardrail (plan §4.2). This event is the CTR DENOMINATOR, so it
   * must carry the same zone as the click or the ratio cannot be sliced.
   * Undefined on an unscoped map search. */
  zone?: string
  /** The cover photo the card served — the cover-photo bandit's arm
   * (spec 2026-09-23). Undefined when the card has no cover. */
  coverPhotoId?: string
  badges?: SearchCardBadges
}): void {
  if (!args.configId) return
  const surface = args.surface ?? 'list_card'
  if (firedOncePerSession(`view:${surface}:${args.configId}`)) return
  ga.event('search_result_view', {
    surface,
    card_layout: surface === 'list_card' ? currentCardLayout() : undefined,
    config_id: args.configId,
    location_id: args.locationId,
    position: args.position,
    zone: args.zone,
    cover_photo_id: args.coverPhotoId,
    ...args.badges
  })
}
